import { useState } from 'react';
import { ChevronDown, ChevronRight, CreditCard, ClipboardCheck, GraduationCap, Wallet, CalendarRange } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';

const ICONES = { formation: CreditCard, tests_selection: ClipboardCheck, journee_preparation: GraduationCap, autre: Wallet };
const fmt = (m) => `${(m || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const fmtDate = (d) => (d ? new Date(String(d).slice(0, 10)).toLocaleDateString('fr-FR') : '-');
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Filtres de la trésorerie : session (année) + période du / au
export const TresorerieFiltres = ({ session, sessions, onSessionChange, periode, onPeriodeChange }) => {
  const now = new Date();
  const raccourcis = [
    ['Ce mois', { debut: iso(new Date(now.getFullYear(), now.getMonth(), 1)), fin: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)) }],
    ['Cette année', { debut: `${now.getFullYear()}-01-01`, fin: `${now.getFullYear()}-12-31` }],
    ['Année dernière', { debut: `${now.getFullYear() - 1}-01-01`, fin: `${now.getFullYear() - 1}-12-31` }],
    ['Tout', { debut: '', fin: '' }],
  ];
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-lg bg-slate-50 border" data-testid="finances-filtres">
      <div>
        <p className="text-xs text-slate-500 mb-1">Session</p>
        <Select value={session} onValueChange={onSessionChange}>
          <SelectTrigger className="w-[200px] h-9 bg-white" data-testid="finances-session-filter">
            <CalendarRange className="h-4 w-4 mr-2 text-slate-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les sessions</SelectItem>
            {sessions.map(s => <SelectItem key={s} value={s}>Session {s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="text-xs text-slate-500 mb-1">Du</p>
        <Input type="date" className="h-9 w-40 bg-white" value={periode.debut} onChange={e => onPeriodeChange({ ...periode, debut: e.target.value })} data-testid="finances-date-debut" />
      </div>
      <div>
        <p className="text-xs text-slate-500 mb-1">Au</p>
        <Input type="date" className="h-9 w-40 bg-white" value={periode.fin} onChange={e => onPeriodeChange({ ...periode, fin: e.target.value })} data-testid="finances-date-fin" />
      </div>
      <div className="flex flex-wrap gap-1">
        {raccourcis.map(([label, p]) => (
          <Button key={label} size="sm" variant={periode.debut === p.debut && periode.fin === p.fin ? 'default' : 'outline'} className="h-9"
            onClick={() => onPeriodeChange(p)} data-testid={`finances-periode-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            {label}
          </Button>
        ))}
      </div>
    </div>
  );
};

// Ligne automatique (formation, tests, préparation…) dépliable avec le détail des paiements
export const GroupeEncaissement = ({ groupe }) => {
  const [ouvert, setOuvert] = useState(false);
  const Icone = ICONES[groupe.cle] || Wallet;
  const Chevron = ouvert ? ChevronDown : ChevronRight;
  return (
    <>
      <tr className="bg-emerald-50 border-b cursor-pointer hover:bg-emerald-100/60" onClick={() => setOuvert(!ouvert)} data-testid={`groupe-encaissement-${groupe.cle}`}>
        <td className="p-3 text-sm text-slate-500"><Chevron className="h-4 w-4 inline" /></td>
        <td className="p-3">
          <div className="flex items-center gap-2">
            <Icone className="h-4 w-4 text-emerald-600" />
            <span className="font-medium text-emerald-700">{groupe.libelle}</span>
            <span className="text-xs text-slate-500">({groupe.details.length} paiement{groupe.details.length > 1 ? 's' : ''})</span>
          </div>
        </td>
        <td className="p-3 text-right font-bold text-emerald-700" data-testid={`groupe-total-${groupe.cle}`}>{fmt(groupe.total)}</td>
        <td className="p-3 text-right">-</td>
        <td className="p-3 text-right text-xs text-slate-400">Auto</td>
      </tr>
      {ouvert && groupe.details.map((d, i) => (
        <tr key={i} className="border-b bg-white text-sm" data-testid={`detail-${groupe.cle}-${i}`}>
          <td className="p-2 pl-8 text-slate-500">{fmtDate(d.date)}</td>
          <td className="p-2">
            <span className="text-slate-800">{d.nom || '—'}</span>
            <span className="text-xs text-slate-400 ml-2">{d.libelle}{d.mode ? ` · ${d.mode}` : ''}</span>
          </td>
          <td className="p-2 text-right text-emerald-600">+{fmt(d.montant)}</td>
          <td className="p-2" colSpan={2}></td>
        </tr>
      ))}
      {ouvert && groupe.details.length === 0 && (
        <tr className="border-b bg-white"><td colSpan={5} className="p-2 pl-8 text-xs text-slate-400">Aucun paiement sur cette période</td></tr>
      )}
    </>
  );
};
